import { z } from "zod";

// ---------------------------------------------------------------------------
// Routines ("Rutiner")
//
// What a department does every day or week to reach its focus goal, who owns
// each routine, and whether it got done. Stored at:
//   Businesses/{businessId}/routines/{routineId}
//   Businesses/{businessId}/routineCompletions/{routineId}_{memberId}_{date}
//   Businesses/{businessId}/routineTemplates/{templateId}
//   Businesses/{businessId}/followUps/{memberId}_{weekStart}
//
// Follow-up suggestions are derived from completion rates, never stored; only
// the "handled" mark is.
// ---------------------------------------------------------------------------

export const RoutineScheduleKind = z.enum(["daily", "weekdays", "weekly"]);
export type RoutineScheduleKind = z.infer<typeof RoutineScheduleKind>;

/** "Dagligen 06:30" = daily/06:30, "mån–fre" = weekdays, "Varje måndag" = weekly/1. */
export const RoutineSchedule = z.object({
  kind: RoutineScheduleKind,
  /** HH:mm, local store time. */
  time: z.string().regex(/^\d{2}:\d{2}$/),
  /** 1 = Monday … 7 = Sunday; weekly only. */
  weekday: z.number().int().min(1).max(7).optional(),
});
export type RoutineSchedule = z.infer<typeof RoutineSchedule>;

export interface RoutineRecord {
  id: string;
  businessId: string;
  salesTeamId: string;
  goalId: string | null;
  name: string;
  schedule: RoutineSchedule;
  /** Roster member ids (salesTeamsMembers). */
  ownerMemberIds: string[];
  requiresPhoto: boolean;
  /**
   * Whether a completion/reminder for this routine goes out over WhatsApp.
   * Default on. Stored and editable (GRA-294's routine editor) as of this
   * commit, but has no consumer yet: "Rutiner over WhatsApp" (GRA-289/290)
   * sends from `DailyTaskRecord` (manager-assigned, no `routineId` link),
   * not from `RoutineRecord`'s own schedule - there is no cron or job
   * anywhere in the repo today that reads a routine's schedule and sends a
   * WhatsApp reminder for it, so this flag doesn't yet gate anything at
   * send time. Confirmed by grepping every consumer of `sendViaWhatsApp`
   * (schema/db/webapp/tRPC only) and every job/trigger under
   * `packages/jobs/src/trigger` and `apps/business-api/functions/src/whatsapp`.
   * Flagging for a follow-up ticket once that send path exists.
   */
  sendViaWhatsApp: boolean;
  templateId: string | null;
  archivedAt: string | null;
  createdBy: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export const Rating = z.number().int().min(1).max(5);

export interface RoutineCompletionRecord {
  /** `${routineId}_${memberId}_${date}`. */
  id: string;
  routineId: string;
  memberId: string;
  salesTeamId: string;
  /** YYYY-MM-DD. */
  date: string;
  done: boolean;
  memberRating: number | null;
  managerRating: number | null;
  note: string | null;
  photoPath: string | null;
  doneAt: string | null;
  ratedByUid: string | null;
}

export interface RoutineTemplateRecord {
  id: string;
  businessId: string;
  name: string;
  kpiDefinitionId: string | null;
  routines: Array<{
    name: string;
    schedule: RoutineSchedule;
    requiresPhoto: boolean;
    sendViaWhatsApp: boolean;
  }>;
  createdBy: string | null;
  createdAt: string | null;
}

export const FollowUpKind = z.enum(["samtal", "paminnelse", "berom"]);
export type FollowUpKind = z.infer<typeof FollowUpKind>;

export interface FollowUpSuggestion {
  memberId: string;
  salesTeamId: string;
  /** Monday, YYYY-MM-DD. */
  weekStart: string;
  /** Done routines / expected routines in the window, 0..1. */
  completionRate: number;
  expected: number;
  done: number;
  kind: FollowUpKind;
  handled: boolean;
  handledAt: string | null;
}

/** < 50 % needs a talk, 50-79 % a reminder, >= 80 % praise. */
export function followUpKindFor(completionRate: number): FollowUpKind {
  if (completionRate < 0.5) return "samtal";
  if (completionRate < 0.8) return "paminnelse";
  return "berom";
}

// ---------------------------------------------------------------------------
// tRPC inputs
// ---------------------------------------------------------------------------

export const ListRoutinesInput = z.object({
  businessId: z.string().min(1),
  salesTeamId: z.string().min(1).optional(),
  includeArchived: z.boolean().optional(),
});
export type ListRoutinesInput = z.infer<typeof ListRoutinesInput>;

export const CreateRoutineInput = z.object({
  businessId: z.string().min(1),
  salesTeamId: z.string().min(1),
  goalId: z.string().min(1).nullable().optional(),
  name: z.string().min(1).max(120),
  schedule: RoutineSchedule,
  ownerMemberIds: z.array(z.string().min(1)).default([]),
  requiresPhoto: z.boolean().default(false),
  sendViaWhatsApp: z.boolean().default(true),
});
export type CreateRoutineInput = z.infer<typeof CreateRoutineInput>;

export const UpdateRoutineInput = z.object({
  businessId: z.string().min(1),
  routineId: z.string().min(1),
  name: z.string().min(1).max(120).optional(),
  schedule: RoutineSchedule.optional(),
  ownerMemberIds: z.array(z.string().min(1)).optional(),
  requiresPhoto: z.boolean().optional(),
  sendViaWhatsApp: z.boolean().optional(),
  goalId: z.string().min(1).nullable().optional(),
});
export type UpdateRoutineInput = z.infer<typeof UpdateRoutineInput>;

export const ArchiveRoutineInput = z.object({
  businessId: z.string().min(1),
  routineId: z.string().min(1),
});
export type ArchiveRoutineInput = z.infer<typeof ArchiveRoutineInput>;

export const ListRoutineCompletionsInput = z.object({
  businessId: z.string().min(1),
  salesTeamId: z.string().min(1).optional(),
  /** YYYY-MM-DD, inclusive. */
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});
export type ListRoutineCompletionsInput = z.infer<typeof ListRoutineCompletionsInput>;

export const SetRoutineCompletionInput = z.object({
  businessId: z.string().min(1),
  routineId: z.string().min(1),
  memberId: z.string().min(1),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  done: z.boolean().optional(),
  memberRating: Rating.nullable().optional(),
  managerRating: Rating.nullable().optional(),
  note: z.string().max(500).nullable().optional(),
  /**
   * The Storage object path the photo was uploaded to (see
   * `routineCompletionPhotoStoragePath` in `@graftus/storage-paths`), for
   * routines with `requiresPhoto`. `routines.setCompletion` rejects a path
   * that does not belong to this exact completion.
   */
  photoPath: z.string().min(1).max(1024).nullable().optional(),
});
export type SetRoutineCompletionInput = z.infer<typeof SetRoutineCompletionInput>;

export const ListRoutineTemplatesInput = z.object({
  businessId: z.string().min(1),
});
export type ListRoutineTemplatesInput = z.infer<typeof ListRoutineTemplatesInput>;

export const CreateRoutineTemplateInput = z.object({
  businessId: z.string().min(1),
  name: z.string().min(1).max(120),
  kpiDefinitionId: z.string().min(1).nullable().optional(),
  routines: z
    .array(
      z.object({
        name: z.string().min(1).max(120),
        schedule: RoutineSchedule,
        requiresPhoto: z.boolean().default(false),
        sendViaWhatsApp: z.boolean().default(true),
      }),
    )
    .min(1),
});
export type CreateRoutineTemplateInput = z.infer<typeof CreateRoutineTemplateInput>;

export const ApplyRoutineTemplateInput = z.object({
  businessId: z.string().min(1),
  templateId: z.string().min(1),
  salesTeamId: z.string().min(1),
  goalId: z.string().min(1).nullable().optional(),
});
export type ApplyRoutineTemplateInput = z.infer<typeof ApplyRoutineTemplateInput>;

export const FollowUpSuggestionsInput = z.object({
  businessId: z.string().min(1),
  salesTeamId: z.string().min(1).optional(),
  /** Monday, YYYY-MM-DD; defaults to the current week. */
  weekStart: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
});
export type FollowUpSuggestionsInput = z.infer<typeof FollowUpSuggestionsInput>;

export const MarkFollowUpHandledInput = z.object({
  businessId: z.string().min(1),
  memberId: z.string().min(1),
  weekStart: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  kind: FollowUpKind,
  handled: z.boolean().default(true),
});
export type MarkFollowUpHandledInput = z.infer<typeof MarkFollowUpHandledInput>;
